import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface Opcao {
  id: number;
  descricao: string;
}

interface ContaReceberItem {
  id: number;
  usuarioPagador: string;
  finDocumento: string;
  previsaoReal: string;
  idCentroCusto: number;
  centroCusto: string;
  parcela: number;
  dataEmissao: string;
  dataVencimento: string;
  dataRecebimento: string;
  valorImposto: number | null;
  valorDocumento: number | null;
  /** documento + imposto + outras despesas + multa + custas + juros − desconto (mesma regra do cadastro / API). */
  valorTotal: number;
  valorOutrasDespesas: number | null;
  valorDesconto: number | null;
  valorMulta: number | null;
  valorCustas: number | null;
  valorJuros: number | null;
  valorRecebido: number | null;
  pago: boolean;
  boletoGerado: boolean;
  cnabEnviado: boolean;
  dre: boolean;
  tipoRecebimento: string;
  observacao: string;
  dataEmissaoOrdenacao: number;
  /** Meia-noite local da data de vencimento (para comparar com «hoje»). */
  dataVencimentoOrdenacao: number;
}

@Component({
  selector: 'app-consultar-contas-receber',
  templateUrl: './consultar-contas-receber.component.html',
  styleUrls: ['./consultar-contas-receber.component.scss']
})
export class ConsultarContasReceberComponent implements OnInit {
  itens: ContaReceberItem[] = [];
  itensPaginados: ContaReceberItem[] = [];

  documentos: Opcao[] = [];
  usuarios: Opcao[] = [];
  centrosCustos: Opcao[] = [];
  tiposRecebimento: Opcao[] = [];

  filtroIdFinDocumento = 0;
  filtroNomePagador = '';
  filtroDataEmissaoInicio = '';
  filtroDataEmissaoFim = '';
  filtroDataVencimentoInicio = '';
  filtroDataVencimentoFim = '';
  filtroDataRecebimentoInicio = '';
  filtroDataRecebimentoFim = '';
  filtroIdTipoRecebimento = 0;
  filtroBoletoGerado: 'T' | 'S' | 'N' = 'T';
  filtroCnabEnviado: 'T' | 'S' | 'N' = 'T';
  /** Todos | Pago (sim) | Em aberto (não) — enviado à API como `pago`. */
  filtroPago: 'T' | 'S' | 'N' = 'T';
  filtroPrevisaoReal: 'T' | 'P' | 'R' = 'T';
  /** Último filtro rápido aplicado (para destaque visual). */
  filtroRapidoAtivo: string | null = null;

  readonly filtrosRapidosList: { id: string; label: string }[] = [
    { id: 'limparPeriodos', label: 'Limpar períodos' },
    { id: 'aberto', label: 'Em aberto' },
    { id: 'vencer7', label: 'A vencer (7 dias)' },
    { id: 'vencer30', label: 'A vencer (30 dias)' },
    { id: 'venceHoje', label: 'Vencem hoje' },
    { id: 'vencidas', label: 'Vencidas (em aberto)' },
    { id: 'vencidas30', label: 'Atraso 30+ dias' },
    { id: 'recebidas', label: 'Pagas' },
    { id: 'recebidasMes', label: 'Recebidas no mês' },
    { id: 'semBoleto', label: 'Sem boleto (aberto)' }
  ];

  /** Blocos de filtro expansíveis (consulta, períodos, atalhos). */
  filtrosBlocoConsultaVisivel = true;
  filtrosBlocoPeriodosVisivel = true;
  filtrosBlocoAtalhosVisivel = true;

  paginaAtual = 1;
  itensPorPagina = 10;
  loading = false;
  arrastandoTabela = false;
  private posicaoInicialX = 0;
  private scrollInicialX = 0;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.carregarCentrosCustos();
    this.buscar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  private carregarCentrosCustos(): void {
    this.ajaxService.get(`${API}financeiro/centrocustos`).subscribe({
      next: (response) => {
        if (response?.hasErrors) {
          return;
        }
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.util.extrairListaResponse(response));
        this.atualizarDescricoesTabela();
      }
    });
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinContaReceber/ConsultarOpcoes`).subscribe({
      next: (response) => {
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar opções.');
          return;
        }
        const data = this.desempacotarResponse(response);
        const documentosData = data.documentos || data.docs || [];
        const usuariosData = data.usuarios || data.clientes || data.usuariospagador || [];
        const centrosCustosData = data.centroscustos || data.centros_custos || data.centros || [];
        const tiposRecebimentoData = data.tiposrecebimento || data.tipopagamento || data.tiposPagamento || [];

        this.documentos = this.normalizarLista(documentosData).map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') })).filter((x: Opcao) => x.id > 0);
        this.usuarios = this.normalizarLista(usuariosData).map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') })).filter((x: Opcao) => x.id > 0);
        this.centrosCustos = this.util.mapearOpcoesCentroCusto(this.normalizarLista(centrosCustosData));
        this.tiposRecebimento = this.normalizarLista(tiposRecebimentoData).map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') })).filter((x: Opcao) => x.id > 0);
        this.atualizarDescricoesTabela();
      }
    });
  }

  novo(): void {
    this.router.navigate(['/contas-receber']);
  }

  alternarBlocoFiltro(bloco: 'consulta' | 'periodos' | 'atalhos'): void {
    if (bloco === 'consulta') {
      this.filtrosBlocoConsultaVisivel = !this.filtrosBlocoConsultaVisivel;
    } else if (bloco === 'periodos') {
      this.filtrosBlocoPeriodosVisivel = !this.filtrosBlocoPeriodosVisivel;
    } else {
      this.filtrosBlocoAtalhosVisivel = !this.filtrosBlocoAtalhosVisivel;
    }
  }

  buscar(): void {
    const params = new URLSearchParams();
    if (this.filtroIdFinDocumento > 0) params.set('idFinDocumento', String(this.filtroIdFinDocumento));
    if (this.filtroNomePagador.trim()) params.set('nomePagador', this.filtroNomePagador.trim());
    const [emiIni, emiFim] = this.ordenarParDatas(this.filtroDataEmissaoInicio, this.filtroDataEmissaoFim);
    if (emiIni) params.set('dataEmissaoInicio', emiIni);
    if (emiFim) params.set('dataEmissaoFim', emiFim);
    const [venIni, venFim] = this.ordenarParDatas(this.filtroDataVencimentoInicio, this.filtroDataVencimentoFim);
    if (venIni) params.set('dataVencimentoInicio', venIni);
    if (venFim) params.set('dataVencimentoFim', venFim);
    const [recIni, recFim] = this.ordenarParDatas(this.filtroDataRecebimentoInicio, this.filtroDataRecebimentoFim);
    if (recIni) params.set('dataRecebimentoInicio', recIni);
    if (recFim) params.set('dataRecebimentoFim', recFim);
    if (this.filtroIdTipoRecebimento > 0) params.set('idTipoRecebimento', String(this.filtroIdTipoRecebimento));
    if (this.filtroBoletoGerado === 'S') {
      params.set('boletoGerado', 'true');
    } else if (this.filtroBoletoGerado === 'N') {
      params.set('boletoGerado', 'false');
    }
    if (this.filtroCnabEnviado === 'S') {
      params.set('cnabEnviado', 'true');
    } else if (this.filtroCnabEnviado === 'N') {
      params.set('cnabEnviado', 'false');
    }
    if (this.filtroPago === 'S') {
      params.set('pago', 'true');
    } else if (this.filtroPago === 'N') {
      params.set('pago', 'false');
    }

    const query = params.toString();
    const url = `${API}financeiro/FinContaReceber/ConsultarFinContaReceber${query ? `?${query}` : ''}`;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        const lista = this.extrairListaResponse(response);
        this.itens = lista.map((x: any) => {
          const item = this.util.converterParaLowercase(x || {});
          const valorImposto = item.valorimposto != null ? Number(item.valorimposto) : null;
          const valorDocumento = item.valordocumento != null ? Number(item.valordocumento) : null;
          const valorOutrasDespesas = item.valoroutrasdespesas != null ? Number(item.valoroutrasdespesas) : null;
          const valorDesconto = item.valordesconto != null ? Number(item.valordesconto) : null;
          const valorMulta = item.valormulta != null ? Number(item.valormulta) : null;
          const valorCustas = item.valorcustas != null ? Number(item.valorcustas) : null;
          const valorJuros = item.valorjuros != null ? Number(item.valorjuros) : null;
          const valorRecebido = item.valorrecebido != null ? Number(item.valorrecebido) : null;
          return {
            id: Number(item.id || 0),
            usuarioPagador: String(item.usuariopagador || ''),
            finDocumento: String(item.findocumento || ''),
            previsaoReal: String(item.previsaoreal || 'R').toUpperCase() === 'P' ? 'P' : 'R',
            idCentroCusto: Number(item.idcentrocusto || 0),
            centroCusto: this.resolverDescricaoPorId(this.centrosCustos, Number(item.idcentrocusto || 0)),
            parcela: Number(item.parcela || 0),
            dataEmissao: this.formatarData(item.dataemissao),
            dataVencimento: this.formatarData(item.datavencimento),
            dataRecebimento: this.formatarData(item.datarecebimento),
            valorImposto,
            valorDocumento,
            valorOutrasDespesas,
            valorDesconto,
            valorMulta,
            valorCustas,
            valorJuros,
            valorRecebido,
            valorTotal: this.calcularValorTotalRecebido({
              valorImposto,
              valorDocumento,
              valorOutrasDespesas,
              valorDesconto,
              valorMulta,
              valorCustas,
              valorJuros
            }),
            pago: this.util.parseBoolean(item.pago),
            boletoGerado: this.util.parseBoolean(item.boletogerado),
            cnabEnviado: this.util.parseBoolean(item.cnabenviado),
            dre: this.util.parseBoolean(item.dre),
            tipoRecebimento: String(item.tiporecebimento || ''),
            observacao: String(item.observacao || ''),
            dataEmissaoOrdenacao: this.obterTimestampData(item.dataemissao),
            dataVencimentoOrdenacao: this.obterTimestampData(item.datavencimento)
          } as ContaReceberItem;
        })
          .filter((x: ContaReceberItem) => this.filtroPrevisaoReal === 'T' || x.previsaoReal === this.filtroPrevisaoReal)
          .filter((x: ContaReceberItem) => x.id > 0)
          .sort((a: ContaReceberItem, b: ContaReceberItem) => {
            if (a.dataEmissaoOrdenacao !== b.dataEmissaoOrdenacao) {
              return b.dataEmissaoOrdenacao - a.dataEmissaoOrdenacao;
            }
            return b.id - a.id;
          });
        this.paginaAtual = 1;
        this.atualizarPaginacao();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.itens = [];
        this.itensPaginados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar contas a receber.');
      }
    });
  }

  editar(item: ContaReceberItem): void {
    this.router.navigate(['/contas-receber'], { queryParams: { id: item.id } });
  }

  pagamento(item: ContaReceberItem): void {
    this.router.navigate(['/contas-receber'], { queryParams: { id: item.id, pagamento: true } });
  }

  formatarMoedaLista(valor: number | null | undefined): string {
    if (valor == null) {
      return '-';
    }
    return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  itemTemDataRecebimentoLista(item: ContaReceberItem): boolean {
    const d = (item.dataRecebimento || '').trim();
    return !!d && d !== '-';
  }

  /** Em aberto e data de vencimento antes de hoje (meia-noite local). */
  itemVencidoEmAberto(item: ContaReceberItem): boolean {
    if (item.pago) return false;
    const ts = item.dataVencimentoOrdenacao;
    if (!ts) return false;
    return ts < this.inicioDoDiaLocalTimestamp(this.hojeLocal());
  }

  /** Um rótulo por linha: recebido (data ou quitado), vencido, à vencer ou em aberto. */
  situacaoRecebidoOuVencido(item: ContaReceberItem): 'recebido' | 'vencido' | 'a_vencer' | 'em_aberto' {
    if (this.itemTemDataRecebimentoLista(item) || item.pago) return 'recebido';
    if (this.itemVencidoEmAberto(item)) return 'vencido';
    const hoje = this.inicioDoDiaLocalTimestamp(this.hojeLocal());
    const ts = item.dataVencimentoOrdenacao;
    if (ts && ts >= hoje) return 'a_vencer';
    return 'em_aberto';
  }

  deletar(item: ContaReceberItem): void {
    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinContaReceber/Deletar`, { id: item.id }).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Registro excluído com sucesso!');
        this.buscar();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao excluir conta a receber.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroIdFinDocumento = 0;
    this.filtroNomePagador = '';
    this.filtroDataEmissaoInicio = '';
    this.filtroDataEmissaoFim = '';
    this.filtroDataVencimentoInicio = '';
    this.filtroDataVencimentoFim = '';
    this.filtroDataRecebimentoInicio = '';
    this.filtroDataRecebimentoFim = '';
    this.filtroIdTipoRecebimento = 0;
    this.filtroBoletoGerado = 'T';
    this.filtroCnabEnviado = 'T';
    this.filtroPago = 'T';
    this.filtroPrevisaoReal = 'T';
    this.filtroRapidoAtivo = null;
    this.buscar();
  }

  aplicarFiltroRapido(id: string): void {
    this.filtroRapidoAtivo = id;
    const hoje = this.hojeLocal();

    const limparPeriodos = (): void => {
      this.filtroDataEmissaoInicio = '';
      this.filtroDataEmissaoFim = '';
      this.filtroDataVencimentoInicio = '';
      this.filtroDataVencimentoFim = '';
      this.filtroDataRecebimentoInicio = '';
      this.filtroDataRecebimentoFim = '';
    };

    switch (id) {
      case 'limparPeriodos':
        limparPeriodos();
        this.filtroPago = 'T';
        this.filtroBoletoGerado = 'T';
        this.filtroCnabEnviado = 'T';
        this.filtroRapidoAtivo = null;
        break;
      case 'aberto':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroBoletoGerado = 'T';
        this.filtroCnabEnviado = 'T';
        break;
      case 'vencer7':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroDataVencimentoInicio = this.dataLocalYmd(hoje);
        this.filtroDataVencimentoFim = this.dataLocalYmd(this.addDiasLocal(hoje, 7));
        break;
      case 'vencer30':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroDataVencimentoInicio = this.dataLocalYmd(hoje);
        this.filtroDataVencimentoFim = this.dataLocalYmd(this.addDiasLocal(hoje, 30));
        break;
      case 'venceHoje':
        limparPeriodos();
        this.filtroPago = 'N';
        const ymd = this.dataLocalYmd(hoje);
        this.filtroDataVencimentoInicio = ymd;
        this.filtroDataVencimentoFim = ymd;
        break;
      case 'vencidas':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroDataVencimentoFim = this.dataLocalYmd(this.addDiasLocal(hoje, -1));
        break;
      case 'vencidas30':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroDataVencimentoFim = this.dataLocalYmd(this.addDiasLocal(hoje, -30));
        break;
      case 'recebidas':
        limparPeriodos();
        this.filtroPago = 'S';
        break;
      case 'recebidasMes': {
        limparPeriodos();
        const ini = this.primeiroDiaMes(hoje);
        const fim = this.ultimoDiaMes(hoje);
        this.filtroDataRecebimentoInicio = this.dataLocalYmd(ini);
        this.filtroDataRecebimentoFim = this.dataLocalYmd(fim);
        this.filtroPago = 'S';
        break;
      }
      case 'semBoleto':
        limparPeriodos();
        this.filtroPago = 'N';
        this.filtroBoletoGerado = 'N';
        this.filtroCnabEnviado = 'T';
        break;
      default:
        this.filtroRapidoAtivo = null;
        break;
    }

    this.buscar();
  }

  filtrarPorPrevisaoReal(tipo: 'T' | 'P' | 'R'): void {
    this.filtroPrevisaoReal = tipo;
    this.buscar();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private ordenarParDatas(a: string, b: string): [string, string] {
    const aa = (a || '').trim();
    const bb = (b || '').trim();
    if (!aa || !bb) {
      return [aa, bb];
    }
    return aa <= bb ? [aa, bb] : [bb, aa];
  }

  private formatarData(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('pt-BR');
  }

  private obterTimestampData(value: any): number {
    if (!value) return 0;
    const texto = String(value).trim();
    const padraoBr = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (padraoBr) {
      const dia = Number(padraoBr[1]);
      const mes = Number(padraoBr[2]);
      const ano = Number(padraoBr[3]);
      return new Date(ano, mes - 1, dia).getTime();
    }

    const data = new Date(texto);
    const timestamp = data.getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }

  private resolverDescricaoPorId(lista: Opcao[], id: number): string {
    if (!id || id <= 0) return '-';
    const encontrado = lista.find((x) => x.id === id);
    return encontrado?.descricao || '-';
  }

  private atualizarDescricoesTabela(): void {
    if (!this.itens?.length) return;
    this.itens = this.itens.map((item) => ({
      ...item,
      centroCusto: this.resolverDescricaoPorId(this.centrosCustos, item.idCentroCusto)
    }));
    this.atualizarPaginacao();
  }

  private hojeLocal(): Date {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }

  private inicioDoDiaLocalTimestamp(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }

  private addDiasLocal(d: Date, dias: number): Date {
    const x = new Date(d.getTime());
    x.setDate(x.getDate() + dias);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate());
  }

  private primeiroDiaMes(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }

  private ultimoDiaMes(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }

  private dataLocalYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Mesma regra de `FinContaReceberBll.CalcularValorRecebido` / cadastro (API CalcularValorRecebido). */
  private calcularValorTotalRecebido(campos: {
    valorImposto: number | null;
    valorDocumento: number | null;
    valorOutrasDespesas: number | null;
    valorDesconto: number | null;
    valorMulta: number | null;
    valorCustas: number | null;
    valorJuros: number | null;
  }): number {
    const n = (v: number | null | undefined) => Number(v || 0);
    let total =
      n(campos.valorDocumento) +
      n(campos.valorImposto) +
      n(campos.valorOutrasDespesas) +
      n(campos.valorMulta) +
      n(campos.valorCustas) +
      n(campos.valorJuros);
    if (campos.valorDesconto != null) {
      total -= Number(campos.valorDesconto);
    }
    return total;
  }
}
